// src/lib/pharmacyFirst/pathways/aht/_shared/tirzepatide-screening-downstream.ts
//
// AHT Weight Management — Tirzepatide (Mounjaro®) shared screening downstream.
// Single source of truth for the post-confirmation steps shared by the initial
// supply and full re-screen pathways: PGD fresh checks, interaction checks,
// cautions review, supply attestation, and the shared outcomes.
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.

import type { OutcomeNode, PathwayNode } from "../../../types/pathway.types";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

export const PGD_FRESH_CHECKS = "tzp-init-pgd-fresh-checks";
export const Q3_INT_NTI       = "tzp-init-int1-narrow-ti";
export const Q4_INT_OCP       = "tzp-init-int2-contraceptives";
export const Q5_CAUTIONS      = "tzp-init-cautions";
export const Q6_ATTEST        = "tzp-init-dose-confirm";

// ─────────────────────────────────────────────────────────────────────────────
// Outcome IDs
// ─────────────────────────────────────────────────────────────────────────────

export const OUT_NO_F2F        = "OUT_CANNOT_NOT_F2F";
export const OUT_CANNOT_SUPPLY = "OUT_CANNOT_SUPPLY";
export const OUT_SUPPLY        = "OUT_SUPPLY";

// ─────────────────────────────────────────────────────────────────────────────
// Shared downstream nodes — spread into a pathway's nodes array after the
// confirmation / exclusion-question stage. The first node is PGD_FRESH_CHECKS.
// ─────────────────────────────────────────────────────────────────────────────

export const tirzepatideScreeningDownstreamNodes: PathwayNode[] = [
  // ───────────────────────────────────────────────────────────────────────────
  // Step 2b — PGD inclusion / exclusion checks (drug-specific fresh checks)
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: PGD_FRESH_CHECKS,
    type: "pgd_exclusion_check",
    variant: "sequential",
    pgdIdOverride: "pgd-aht-wt-tirzepatide",
    anyTriggered: { outcomeId: OUT_CANNOT_SUPPLY },
    allCleared: Q3_INT_NTI,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 3 — Interaction checks
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q3_INT_NTI,
    type: "yes_no",
    text: "Is the patient taking any oral medicines with a narrow therapeutic index?",
    hint: "Tirzepatide delays gastric emptying — may affect absorption of oral medicines.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document the monitoring and management plan for this interaction",
      acknowledgementText: "Patient has been counselled on this interaction and understands the management plan",
    },
    yes: Q4_INT_OCP,
    no: Q4_INT_OCP,
  },

  {
    id: Q4_INT_OCP,
    type: "yes_no",
    text: "Is the patient using oral contraceptives?",
    hint: "Reduced efficacy cannot be excluded — add barrier method for 4 weeks after starting or any dose increase.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document the contraceptive counselling and management plan",
      acknowledgementText: "Patient has been counselled on this interaction and understands the management plan",
    },
    yes: Q6_ATTEST,
    no: Q6_ATTEST,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 5 — Supply attestation
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q6_ATTEST,
    type: "attestation",
    text: "Step 5 — Confirm supply of Mounjaro® 2.5mg KwikPen",
    attestationStatement:
      "I confirm that I have:\n" +
      "• Checked pen integrity and cold chain before supply\n" +
      "• Confirmed storage: 2–8°C before first use; after first use ≤30°C or refrigerated up to 30 days\n" +
      "• Counselled the patient on subcutaneous injection technique and injection site rotation\n" +
      "• Counselled the patient on missed dose guidance\n" +
      "• Advised the patient to inform their GP of initiation\n" +
      "• Provided patient information leaflet\n" +
      "• Recorded batch number and expiry date",
    conclusions: {
      optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
      optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Shared outcomes — used by both the initial supply and re-screen pathways.
// ─────────────────────────────────────────────────────────────────────────────

export const tirzepatideScreeningOutcomes: Record<string, OutcomeNode> = {
  [OUT_NO_F2F]: {
    id: OUT_NO_F2F,
    type: "outcome",
    outcomeType: "cannot_supply",
    title: "Cannot supply — face-to-face consultation required",
    reason:
      "Supply under this PGD requires the patient to attend in person at AHT registered premises. Virtual or online consultations are not permitted.",
    safetyNetting: [
      "Ask the patient to book an in-person appointment to proceed with supply.",
    ],
  },
  [OUT_CANNOT_SUPPLY]: {
    id: OUT_CANNOT_SUPPLY,
    type: "outcome",
    outcomeType: "cannot_supply",
    title: "Cannot supply — Mounjaro® (tirzepatide)",
    reason:
      "An inclusion criterion was not met or an exclusion criterion was triggered. Supply cannot proceed under this PGD.",
    safetyNetting: [
      "Discuss the reason supply cannot proceed with the patient.",
      "Consider referral to prescriber or GP if clinically appropriate.",
    ],
  },
  [OUT_SUPPLY]: {
    id: OUT_SUPPLY,
    type: "outcome",
    outcomeType: "supply",
    title: "Supply complete — Mounjaro® 2.5mg (initial supply)",
    reason:
      "All inclusion criteria met, no exclusion criteria triggered, and supply attestation confirmed.",
    drugRef: "tirzepatide",
    safetyNetting: [
      "Return in 4 weeks for monthly monitoring review.",
      "Contact the service immediately if any signs of allergic reaction, severe abdominal pain, or persistent vomiting.",
      "Seek urgent medical attention for signs of pancreatitis: severe, persistent abdominal pain radiating to the back.",
      "Store unused pens in the refrigerator (2–8°C). After first use, may store at ≤30°C for up to 30 days.",
    ],
  },
};
